import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../../../layout';
import SettlementTable from './Table/settlement';
import SalesTable from './Table/sales';
import DeliveryTable from './Table/delivery';
import { EstimatedAmountDetailPage } from '../style';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import { requestApi } from '../../../../utils/request';
import ROUTE_PATH from '../../../../routes/config';
import dayjs from 'dayjs';
import { formatNumberWithCommas } from '../../../../tools/NumberTool';
import { CheckBoxCustom } from 'components/CheckBox/style';
import { useUI } from '../../../../contexts/UIContext';

const EstimatedAmountDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setContextLoading, setModalData } = useUI();
  const [tabIndex, setTabIndex] = useState('settlement'); //settlement, sales, delivery
  const [detailData, setDetailData] = useState({});
  const { dataProviderSeq, mallAccountId, mallSeq } = location.state || {};
  const scrollRef = useRef(null);

  const tabs = [
    {
      key: 'settlement',
      label: '정산금 정보',
      content: <SettlementTable tableData={detailData.settlementInfo} />,
    },
    {
      key: 'sales',
      label: '매출액 정보',
      content: <SalesTable tableData={detailData.salesInfo} />,
    },
    {
      key: 'delivery',
      label: '배송완료 기준\n정산금 정보',
      content: (
        <DeliveryTable
          tableData={detailData.deliverySettlementInfo}
          setModalData={setModalData}
        />
      ),
    },
  ];

  const onChangeCheckBox = event => {
    const { checked } = event.target;
    (async () => {
      await fetchManageMallDetail({
        dataProviderSeq,
        mallAccountId,
        mallSeq,
        deliverSuccessAmountYn: checked ? 'Y' : 'N',
      });
    })();
  };

  const fetchManageMallDetail = async ({
    dataProviderSeq,
    mallAccountId,
    mallSeq,
    deliverSuccessAmountYn = 'Y',
  }) => {
    try {
      const res = await requestApi({
        url: `/v1/au/data-provider/manage/mall/settlement-scheduled`,
        method: 'GET',
        params: {
          dataProviderSeq: dataProviderSeq,
          mallAccountId: mallAccountId,
          mallSeq: mallSeq,
          deliverSuccessAmountYn: deliverSuccessAmountYn,
        },
      });
      if (res.success) {
        setDetailData(res.data.result);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setContextLoading(false);
    }
  };

  // 탭 이동시 스크롤 위치 초기화
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [tabIndex]);

  useEffect(() => {
    (async () => {
      await fetchManageMallDetail({ dataProviderSeq, mallAccountId, mallSeq });
    })();
  }, []);

  useEffect(() => {
    if (!location.state) {
      alert('잘못된 접근입니다.');
      navigate(ROUTE_PATH.mypage.estimatedAmountManage);
    }
  }, [location, navigate]);

  return (
    <Layout pageTitle={'정산예정금 상세'}>
      <EstimatedAmountDetailPage>
        <div className="updateDate">
          최신 업데이트 일시 :{' '}
          {dayjs(detailData.lastUpdateDt).format('YYYY.MM.DD HH:mm:ss')}
        </div>
        <section className="mallInfo">
          <p className="title">판매몰 정보</p>
          <dl>
            <dt>판매몰</dt>
            <dd>{detailData.mallInfo?.mallName}</dd>
            <dt>판매몰 아이디</dt>
            <dd>{detailData.mallInfo?.mallAccountId}</dd>
            <dt>최근 1년 총 매출 합계</dt>
            <dd>
              {formatNumberWithCommas(detailData.mallInfo?.sumSalesAmt)} 원
            </dd>
            <dt>
              최근 1년 연환산 매출액{' '}
              <button
                type="button"
                onClick={() => {
                  setModalData({
                    textAlign: 'left',
                    fontWeight: 400,
                    contents: `'최근 1년 연환산 매출액'은 대출 비교 한도 계산에 활용되는 금액입니다.\n\u00A0• 1년 미만의 매출 데이터만 있는 경우, '최근 1년 총 매출합계'로 계산하면 한도가 적을 수 있으므로, 연환산 방식이 적용됩니다.\n\u00A0• 연환산 방식은 해당 기간의 월평균 매출을 계산해 12개월 기준으로 환산하며, 마지막 달이 종료되지 않았다면 해당 월의 매출은 포함되지 않습니다.`,
                  });
                }}
              >
                <FeatherIcon
                  icon="help-circle"
                  size={16}
                  stroke="#737171"
                  strokeWidth={2}
                />
              </button>
            </dt>
            <dd>{detailData.mallInfo?.annualSalesAmt.toLocaleString()} 원</dd>
          </dl>
        </section>
        <section className="estimatedInfo">
          <p className="title">정산예정금 정보</p>
          <ul>
            {detailData.scheduledSettlementInfo?.map((item, index) => {
              return (
                <li key={index}>
                  <dl>
                    <dt>{item.scheduledSettlementDate}</dt>
                    <dd>
                      {item.scheduledSettlementDateAmt.toLocaleString()} 원
                    </dd>
                  </dl>
                </li>
              );
            })}
          </ul>
          <dl className="total">
            <dt>합계</dt>
            <dd>
              {detailData.sumScheduledSettlementDateAmt?.toLocaleString()} 원
            </dd>
          </dl>
          <CheckBoxCustom className="checkBox">
            <input
              type="checkbox"
              id="deliverSuccessAmountYn"
              defaultChecked
              onChange={onChangeCheckBox}
            />
            <FeatherIcon
              icon="check"
              size={14}
              stroke="white"
              className="checkIcon"
            />
            <span>배송완료금액 포함</span>
            <button
              type="button"
              onClick={() => {
                setModalData({
                  textAlign: 'left',
                  fontWeight: 400,
                  contents: `'배송완료금액'은 배송 완료 상태로 확정 된 날짜를 기준으로 정산 예정일을 임의로 산출한 금액이므로, 참고용으로만 활용해 주세요.`,
                });
              }}
            >
              <FeatherIcon
                icon="help-circle"
                size={16}
                stroke="#737171"
                strokeWidth={2}
              />
            </button>
          </CheckBoxCustom>
        </section>
        <section className="settlementInfo">
          <p className="title">정산/매출 정보</p>
          <div className="tabBox">
            {tabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                className={tabIndex === tab.key ? 'active' : ''}
                onClick={() => setTabIndex(tab.key)}
              >
                {tab.label.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index !== tab.label.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </button>
            ))}
          </div>
          <div className="tableWrap" ref={scrollRef}>
            {tabs.find(tab => tab.key === tabIndex)?.content}
          </div>
        </section>
      </EstimatedAmountDetailPage>
    </Layout>
  );
};

export default EstimatedAmountDetail;
